import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './RoutineCards.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://iron.nextriking.com/api';
const CARDS_URL = `${API_BASE_URL}/routines/cards`;

const createExercise = () => ({ exercise: '', sets: '3', reps: '10-12', weight: '', rest: '60 s', equipment: '', notes: '' });

const createDay = (number) => ({
  title: `DÍA ${number}`,
  focus: '',
  exercises: Array.from({ length: 6 }, createExercise),
  record: '',
  observations: '',
});

const fieldLabels = [
  ['exercise', 'Ejercicio'],
  ['sets', 'Series'],
  ['reps', 'Repeticiones'],
  ['weight', 'Peso'],
  ['rest', 'Descanso'],
  ['equipment', 'Máquina / material'],
  ['notes', 'Indicaciones / alternativa'],
];

const RoutineCards = () => {
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState({ level: '', objective: '', professor: '' });
  const [days, setDays] = useState([1, 2, 3, 4].map(createDay));
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      const [usersResponse, templatesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/users`),
        axios.get(`${CARDS_URL}/templates`),
      ]);
      setUsers(Array.isArray(usersResponse.data.body) ? usersResponse.data.body : []);
      setTemplates(Array.isArray(templatesResponse.data.body) ? templatesResponse.data.body : []);
    } catch (fetchError) {
      setError('No se pudieron cargar los alumnos o las tarjetas guardadas.');
      console.error('Error al cargar tarjetas de rutinas:', fetchError);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateDetail = (event) => {
    setDetails((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const updateDay = (dayIndex, field, value) => {
    setDays((current) => current.map((day, index) => index === dayIndex ? { ...day, [field]: value } : day));
  };

  const updateExercise = (dayIndex, exerciseIndex, field, value) => {
    setDays((current) => current.map((day, index) => {
      if (index !== dayIndex) return day;
      return {
        ...day,
        exercises: day.exercises.map((exercise, itemIndex) => itemIndex === exerciseIndex
          ? { ...exercise, [field]: value }
          : exercise),
      };
    }));
  };

  const addExercise = (dayIndex) => {
    setDays((current) => current.map((day, index) => index === dayIndex
      ? { ...day, exercises: [...day.exercises, createExercise()] }
      : day));
  };

  const removeExercise = (dayIndex, exerciseIndex) => {
    setDays((current) => current.map((day, index) => {
      if (index !== dayIndex || day.exercises.length === 1) return day;
      return { ...day, exercises: day.exercises.filter((_, itemIndex) => itemIndex !== exerciseIndex) };
    }));
  };

  const cardData = () => ({
    title: title.trim(),
    details: { ...details },
    days,
  });

  const saveTemplate = async () => {
    if (!title.trim()) {
      setError('Ingresá un nombre para guardar la tarjeta.');
      return;
    }

    try {
      const response = await axios.post(`${CARDS_URL}/templates`, cardData());
      const templateId = response.data.body?.id;
      await fetchData();
      if (templateId) setSelectedTemplate(String(templateId));
      setError(null);
      setMessage('Tarjeta guardada correctamente.');
    } catch (saveError) {
      setMessage(null);
      setError('No se pudo guardar la tarjeta.');
      console.error('Error al guardar tarjeta:', saveError);
    }
  };

  const loadTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find((item) => String(item.id) === String(templateId));
    if (!template) return;
    setTitle(template.title);
    setDetails(template.details || { level: '', objective: '', professor: '' });
    setDays(Array.isArray(template.days) && template.days.length ? template.days : [1, 2, 3, 4].map(createDay));
    setError(null);
  };

  const assignTemplate = async () => {
    if (!selectedUser || !selectedTemplate) {
      setError('Seleccioná un alumno y una tarjeta guardada para asignarla.');
      return;
    }

    try {
      await axios.post(`${CARDS_URL}/templates/${selectedTemplate}/assignments`, { userId: selectedUser });
      setError(null);
      setMessage('Tarjeta asignada correctamente.');
    } catch (assignError) {
      setMessage(null);
      setError('No se pudo asignar la tarjeta.');
      console.error('Error al asignar tarjeta:', assignError);
    }
  };

  const selectedMember = users.find((user) => String(user.id) === String(selectedUser));
  const memberName = selectedMember ? [selectedMember.name, selectedMember.lastname].filter(Boolean).join(' ') : '';

  return (
    <main className="routine-cards-page">
      <section className="routine-cards-toolbar no-print">
        <div>
          <span className="routine-cards-kicker">IRON MUSCLE LAB</span>
          <h1>Tarjetas de rutinas</h1>
          <p>Completá, guardá y asigná la rutina; también podés imprimirla en formato de ficha.</p>
        </div>
        <div className="routine-cards-actions">
          <Link to="/dashboard" className="routine-cards-back">Volver al panel</Link>
          <button type="button" className="routine-cards-print" onClick={() => window.print()}>Imprimir tarjetas</button>
        </div>
      </section>

      <section className="routine-cards-editor no-print">
        <h2>Datos de la rutina</h2>
        {error && <p className="routine-cards-feedback routine-cards-feedback--error" role="alert">{error}</p>}
        {message && <p className="routine-cards-feedback routine-cards-feedback--success">{message}</p>}
        <div className="routine-cards-details">
          <label>Nombre de la tarjeta<input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ej.: Fuerza 4 días" /></label>
          <label>Alumno<select value={selectedUser} onChange={(event) => setSelectedUser(event.target.value)}><option value="">Seleccioná un alumno...</option>{users.map((user) => <option key={user.id} value={user.id}>{[user.name, user.lastname].filter(Boolean).join(' ') || `Alumno ${user.id}`}</option>)}</select></label>
          <label>Nivel<input name="level" value={details.level} onChange={updateDetail} placeholder="Ej.: Intermedio" /></label>
          <label>Objetivo<input name="objective" value={details.objective} onChange={updateDetail} placeholder="Ej.: Hipertrofia" /></label>
          <label>Profesor<input name="professor" value={details.professor} onChange={updateDetail} placeholder="Nombre del profesor" /></label>
        </div>
        <div className="routine-cards-assignment">
          <label>Tarjeta guardada<select value={selectedTemplate} onChange={(event) => loadTemplate(event.target.value)}><option value="">Seleccioná una tarjeta...</option>{templates.map((template) => <option key={template.id} value={template.id}>{template.title}</option>)}</select></label>
          <button type="button" className="routine-cards-save" onClick={saveTemplate}>Guardar tarjeta</button>
          <button type="button" className="routine-cards-assign" onClick={assignTemplate}>Asignar a alumno</button>
        </div>

        {days.map((day, dayIndex) => (
          <article className="routine-day-editor" key={dayIndex}>
            <div className="routine-day-editor-title">
              <label>Día<input value={day.title} onChange={(event) => updateDay(dayIndex, 'title', event.target.value)} /></label>
              <label>Enfoque / zona<input value={day.focus} onChange={(event) => updateDay(dayIndex, 'focus', event.target.value)} placeholder="Ej.: Torso 1" /></label>
            </div>
            <div className="routine-exercise-editor">
              {day.exercises.map((exercise, exerciseIndex) => (
                <div className="routine-exercise-row" key={exerciseIndex}>
                  {fieldLabels.map(([field, label]) => (
                    <label key={field}>{label}<input value={exercise[field]} onChange={(event) => updateExercise(dayIndex, exerciseIndex, field, event.target.value)} /></label>
                  ))}
                  <button type="button" className="routine-remove" onClick={() => removeExercise(dayIndex, exerciseIndex)} aria-label="Quitar ejercicio">×</button>
                </div>
              ))}
            </div>
            <button type="button" className="routine-add" onClick={() => addExercise(dayIndex)}>+ Agregar ejercicio</button>
            <div className="routine-day-notes">
              <label>Registro de carga<input value={day.record} onChange={(event) => updateDay(dayIndex, 'record', event.target.value)} placeholder="Ej.: Carga inicial: ____ kg" /></label>
              <label>Observaciones<textarea value={day.observations} onChange={(event) => updateDay(dayIndex, 'observations', event.target.value)} /></label>
            </div>
          </article>
        ))}
      </section>

      <section className="routine-print-area">
        {[0, 2].map((start) => (
          <div className="routine-print-sheet" key={start}>
            {days.slice(start, start + 2).map((day) => (
              <article className="routine-print-card" key={day.title}>
                <header className="routine-card-header">
                  <div className="routine-card-brand"><span>IM</span><strong>IRON</strong><small>MUSCLE LAB</small></div>
                  <div className="routine-card-title"><h2>RUTINA DE ENTRENAMIENTO</h2><p>DISCIPLINA · CONSTANCIA · RESULTADOS</p></div>
                </header>
                <section className="routine-card-info">
                  <p><strong>NOMBRE:</strong> {memberName || '________________________________'}</p>
                  <p><strong>NIVEL:</strong> {details.level || '________________'}</p>
                  <p><strong>OBJETIVO:</strong> {details.objective || '____________________________'}</p>
                  <p><strong>PROFESOR:</strong> {details.professor || '______________________'}</p>
                </section>
                <div className="routine-card-day"><strong>{day.title}</strong><span>{day.focus || 'ENFOQUE / ZONA'}</span></div>
                <table className="routine-card-table">
                  <thead><tr>{fieldLabels.map(([, label]) => <th key={label}>{label}</th>)}</tr></thead>
                  <tbody>{day.exercises.map((exercise, index) => <tr key={index}>{fieldLabels.map(([field]) => <td key={field}>{exercise[field]}</td>)}</tr>)}</tbody>
                </table>
                <footer className="routine-card-footer">
                  <p><strong>REGISTRO:</strong> {day.record || 'Carga: __________ kg     Fecha: __________'}</p>
                  <p><strong>OBSERVACIONES:</strong> {day.observations || '_________________________________________________________________'}</p>
                </footer>
              </article>
            ))}
          </div>
        ))}
      </section>
    </main>
  );
};

export default RoutineCards;
